import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { EMT_PAGE_NAMES, LAST_PATH, OPEN_GRAPH_FACEBOOK_ADMIN } from '../../constants';
import { generateStorePageCanonical, generateStorePageTitle } from '../../helpers/pps-helpers';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import { emtContentModel, storeSearchModel } from '../../models';
import { generateTags } from '../../utils/generate-tags';
import { generateProDeskDesc, getProDeskSchemas } from './pro-desk-helpers';

// --- Tag Handler ---

const proDeskHandler = ({ data: { emtData, storeData }, props: { storeId } }) => {
  // Extract store details data once
  const storeInfo = {
    ...storeData?.storeSearch?.stores?.[0],
    storeId
  };
  const {
    name: storeName,
    address: { city, postalCode, state } = {},
    storeDetailsPageLink
  } = storeInfo;

  const canonicalURL = generateStorePageCanonical({
    storeDetailsPageLink, urlSuffix: LAST_PATH.PRO_DESK
  });

  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.proDeskMetadata || {};

  const fallbackDescription = generateProDeskDesc({ storeName });
  const fallbackTitle = generateStorePageTitle({
    department: 'Pro Desk', city, storeName, postalCode, state
  });
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage,
    ogTitle = fallbackTitle,
    ogType = 'PPS-Pro-Desk',
    schema: emtSchema,
    title = fallbackTitle
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const schemas = getProDeskSchemas({ emtSchema, storeInfo });

  const preloadImages = [{
    href: 'https://dam.thdstatic.com/content/production/UzFpCaOH5bTq4fxt_ur3sQ/LtpQZzzYDZ5N3i-gV-W9tQ/'
      + 'Original%20file/WF-6162000-Pro-Store-Page-Hero-Banner.jpg?Resize=(899.33,180)'
  }];

  const tags = generateTags({
    canonical: canonicalURL,
    description: metaDescription,
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    ogDescription,
    ogImage,
    ogSiteName: 'The Home Depot',
    ogTitle,
    ogType,
    ogUrl: canonicalURL,
    preloadImages,
    schemas,
    title
  });

  return tags;
};

// --- Main Component ---

const ProDeskMetadata = ({ storeId }) => {
  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.PRO_DESK
    }
  };
  const storeSearchOptions = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);
  const { data: storeData } = useDataModel('storeSearch', storeSearchOptions);

  const data = { emtData, storeData };
  const props = { storeId };

  useHelmet('proDeskMetadata', { data, props, skipCleanup: true }, proDeskHandler, [storeId]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

ProDeskMetadata.displayName = 'ProDeskMetadata';

ProDeskMetadata.dataModel = extend(
  emtContentModel,
  storeSearchModel
);

ProDeskMetadata.propTypes = {
  storeId: PropTypes.string.isRequired
};

export { ProDeskMetadata };